import { completeStage, selectStage } from '../../../redux/slices/stageSlice';
import React, { useEffect, useMemo, useState } from 'react';
import {
	requestOtpThunk,
	sendPrecheckinLinkThunk,
	verifyOtpThunk,
} from '../../../redux/slices/otpValidationSlice';
import Select, { StylesConfig } from 'react-select';
import {
	selectProfile,
	setCurrentProfile,
} from '../../../redux/slices/profileSlice';
import {
	selectSession,
	setGuestVerificationLazyLoad,
	upsertProfileVerificationStatusThunk,
} from '../../../redux/slices/sessionSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

import { bypassConfirmLeaveOnce } from '../../../hooks/confirmLeavePageBypass';
import { DarkButton } from '../../../components/dark-button';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PrecheckinLinkSentModal from '../../../app/guest-otp-validation/components/link-sent';
import styles from '../page.module.css';
import { t } from 'i18next';

interface GuestOtpValidationFormProps {
	guestWithMe: boolean | null;
}
type AgeOption = {
	value: string;
	label: string;
};
const RESEND_COOLDOWN_SECONDS = 30;
const MAX_WRONG_OTP_ATTEMPTS = 3;

const buildFullName = (profile: any) =>
	[profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();

const splitFullName = (fullName: string) => {
	const parts = fullName.trim().split(/\s+/).filter(Boolean);

	if (parts.length === 0) {
		return { firstName: '', lastName: '' };
	}

	if (parts.length === 1) {
		return { firstName: parts[0], lastName: '' };
	}

	return {
		firstName: parts[0],
		lastName: parts.slice(1).join(' '),
	};
};
const customStyles: StylesConfig<AgeOption, false> = {
	control: (provided: any, state: any) =>
		({
			...provided,
			borderColor: 'var(--bs-input-border-color)',
			boxShadow: state.isFocused
				? `0 0 0 1px var(--bs-input-border-color)`
				: 'none',
			fontSize: '0.8em',
			minHeight: '38px', // adjust height
		} as any),
	valueContainer: (provided: any) =>
		({
			...provided,
			padding: '0.375rem 0.75rem', // âœ… this actually affects input padding
		} as any),
	input: (provided: any) =>
		({
			...provided,
			margin: '0', // optional, remove extra spacing inside input
			padding: '0', // optional
		} as any),
};

const searchableSelectStyles: StylesConfig<AgeOption, false> = {
	...customStyles,
	valueContainer: (provided: any) =>
		({
			...provided,
			padding: '0 0.75rem',
			overflow: 'visible',
		} as any),
	input: (provided: any) =>
		({
			...provided,
			margin: '0',
			padding: '0',
		} as any),
	placeholder: (provided: any) =>
		({
			...provided,
			margin: '0',
		} as any),
	singleValue: (provided: any) =>
		({
			...provided,
			margin: '0',
		} as any),
};
const GuestOtpValidationForm = ({
	guestWithMe,
}: GuestOtpValidationFormProps) => {
	const [linkSent, setLinkSent] = useState(false);
	const [precheckinLink, setPrecheckinLink] = useState<string | null>(null);

	const [lazyLoad, setLazyLoad] = useState(false);
	const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
	const dispatch = useAppDispatch();
	const { token, reservation, propertyInfo, profiles } =
		useAppSelector(selectSession);
	const { stages, stage } = useAppSelector(selectStage);

	const [showOtpScreen, setShowOtpScreen] = useState(false);
	const [showError, setShowError] = useState(false);
	const [otpError, setOtpError] = useState(false);
	const [submissionMessage, setSubmissionMessage] = useState('');
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	const [fullName, setFullName] = useState('');
	const [nameError, setNameError] = useState('');
	const [otp, setOtp] = useState(['', '', '', '']);
	const [isResendingOtp, setIsResendingOtp] = useState(false);
	const [resendCooldown, setResendCooldown] = useState(0);
	const [showResendSuccess, setShowResendSuccess] = useState(false);
	const [wrongOtpAttempts, setWrongOtpAttempts] = useState(0);
	const { currentProfile } = useAppSelector(selectProfile);
	const isChild = Boolean(currentProfile?.isChild);
	const [otpExpiry, setOtpExpiry] = useState<number | null>(null); // timestamp in ms
	const [otpCountdown, setOtpCountdown] = useState<number>(0); // seconds left

	const [selectedAge, setSelectedAge] = useState<AgeOption | null>(null);
	const [parentalConsent, setParentalConsent] = useState(false);
	const ageOptions: AgeOption[] = [
		{
			value: '1-11',
			label: t('GuestDetailsDropdownAgeGroup1'),
		},
		{
			value: '12-17',
			label: t('GuestDetailsDropdownAgeGroup2'),
		},
	];

	const isOtpExpired = otpCountdown <= 0;

	const DEFAULT_OTP_EXPIRY_SECONDS = 1 * 60;

	const getExpirySeconds = (response: any): number => {
		const raw =
			response?.expirySeconds ??
			response?.data?.expirySeconds ??
			response?.data?.data?.expirySeconds ??
			response?.data?.result?.expirySeconds ??
			response?.result?.expirySeconds;
		const parsed = Number(raw);
		return Number.isFinite(parsed) && parsed > 0
			? parsed
			: DEFAULT_OTP_EXPIRY_SECONDS;
	};

	//#region parent dropdown
	const parentOptions = profiles
		?.filter((p: any) => p.isChild === false)
		.map((p: any) => ({
			value: p.profileID,
			label: `${p.firstName} ${p.lastName}`,
		}));

	const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
	const selectedParent =
		parentOptions?.find((option: { value: number; label: string }) => {
			return option.value === selectedParentId;
		}) ?? null;

	useEffect(() => {
		const primary = profiles?.find((p: any) => p.isPrimary);
		if (primary) {
			setSelectedParentId((currentParentId) => {
				if (
					currentParentId !== null &&
					profiles?.some((p: any) => p.profileID === currentParentId)
				) {
					return currentParentId;
				}

				return primary.profileID;
			});
		}
	}, [profiles]);
	//#endregion

	useEffect(() => {
		if (!isChild || selectedParentId === null || !profiles) return;
		const parentProfile = profiles.find(
			(p: any) => p.profileID === selectedParentId
		);
		setEmail(parentProfile?.email || '');
	}, [isChild, selectedParentId, profiles]);

	useEffect(() => {
		if (currentProfile !== null && currentProfile !== undefined) {
			setEmail(currentProfile.email);
			setFullName(buildFullName(currentProfile));
		}
	}, [currentProfile]);

	useEffect(() => {
		setShowError(false);
	}, [showOtpScreen]);

	useEffect(() => {
		if (resendCooldown <= 0) return;
		const timer = setTimeout(() => {
			setResendCooldown((prev) => Math.max(prev - 1, 0));
		}, 1000);
		return () => clearTimeout(timer);
	}, [resendCooldown]);

	useEffect(() => {
		if (!showResendSuccess) return;
		const timer = setTimeout(() => {
			setShowResendSuccess(false);
		}, 4000);
		return () => clearTimeout(timer);
	}, [showResendSuccess]);

	const arabicCharactersPattern =
		/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
	const emailPattern =
		/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;
	const isEmailValid = emailPattern.test(email?.trim());
	const shouldValidateEmail = !showOtpScreen;
	const isParentalConsentRequired =
		!showOtpScreen &&
		guestWithMe !== false &&
		isChild &&
		selectedAge?.value === '12-17';
	const isActionDisabled = isParentalConsentRequired && !parentalConsent;

	const handleCompleteGuestDetails = () => {
		handleRequestOtp();
	};

	useEffect(() => {
		// setChildSubmissionMessage('');
		setParentalConsent(false);
	}, [selectedAge]);

	// const handleChildSubmission = () => {
	// 	if (!selectedAge) {
	// 		setChildSubmissionMessage('Please select an age group.');
	// 		setLazyLoad(false); // ensure button not loading
	// 		return;
	// 	}

	// 	// if (selectedAge.value === '1-11') {
	// 	// 	setChildSubmissionMessage(
	// 	// 		'Please proceed to the reception for manual check-in.'
	// 	// 	);
	// 	// 	dispatch(
	// 	// 		completeStage({
	// 	// 			token,
	// 	// 			stageID: stage?.stepID,
	// 	// 			ReservationID: reservation?.ReservationID,
	// 	// 			type: 'checkin',
	// 	// 			confirmationNo: reservation?.PmsConfirmationNo,
	// 	// 			stages,
	// 	// 			propertyCode: propertyInfo?.propertyCode ?? '',
	// 	// 			profileID: currentProfile?.profileID ?? null,
	// 	// 		})
	// 	// 	)
	// 	// 		.unwrap()
	// 	// 		.then(() => window.location.reload())
	// 	// 		.catch(() => setShowError(true));

	// 	// 	setLazyLoad(false);
	// 	// 	return;
	// 	// }

	// 	if (selectedAge.value === '1-11') {
	// 		setChildSubmissionMessage(
	// 			'Please proceed to the reception for manual check-in.'
	// 		);

	// 		setLazyLoad(true);

	// 		dispatch(
	// 			completeStage({
	// 				token,
	// 				stageID: stage?.stepID,
	// 				ReservationID: reservation?.ReservationID,
	// 				type: 'checkin',
	// 				confirmationNo: reservation?.PmsConfirmationNo,
	// 				stages,
	// 				propertyCode: propertyInfo?.propertyCode ?? '',
	// 				profileID: currentProfile?.profileID ?? null,
	// 			})
	// 		)
	// 			.unwrap()
	// 			.then(() => {
	// 				/* eslint-disable no-debugger */
	// 				debugger;
	// 				// ✅ Call second API AFTER first succeeds
	// 				return dispatch(
	// 					upsertProfileVerificationStatusThunk({
	// 						token,
	// 						profile: {
	// 							...currentProfile,
	// 							VerificationStatus: 'Completed', // 👈 force it
	// 						},
	// 					})
	// 				).unwrap();
	// 			})
	// 			.then((res: any) => {
	// 				console.log('Profile upserted, response:', res);

	// 				if (res?.result) {
	// 					console.log('Profile upserted successfully');
	// 				}

	// 				// ✅ Reload only after BOTH calls succeed
	// 				window.location.reload();
	// 			})
	// 			.catch((err) => {
	// 				console.error(err);
	// 				setShowError(true);
	// 			})
	// 			.finally(() => {
	// 				setLazyLoad(false);
	// 			});

	// 		return;
	// 	}

	// 	if (selectedAge.value === '12-17') {
	// 		if (!parentalConsent) {
	// 			setChildSubmissionMessage('Parental Consent is mandatory.');
	// 			setLazyLoad(false);
	// 			return;
	// 		}
	// 		/* eslint-disable no-debugger */
	// 		debugger;
	// 		setChildSubmissionMessage('');
	// 		handleRequestOtp(); // this will set lazyLoad true properly
	// 		return;
	// 	}
	// };

	const handleChildSubmission = async () => {
		if (!selectedAge) return; // button already disabled, just safety guard

		// 👶 Age 1–11 → manual check-in
		if (selectedAge.value === '1-11') {
			try {
				// setLazyLoad(true);

				const { firstName, lastName } = splitFullName(fullName);

				dispatch(setGuestVerificationLazyLoad(true));
				dispatch(
					upsertProfileVerificationStatusThunk({
						token,
						profile: {
							...currentProfile,
							VerificationStatus: 'Completed',
							IsManualVerification: true,
							firstName,
							lastName,
							fullName: fullName.trim(),
						},
					})
				)
					.unwrap()
					.then(() => {
						dispatch(
							completeStage({
								token,
								stageID: stage?.stepID,
								ReservationID: reservation?.ReservationID,
								type: 'checkin',
								confirmationNo: reservation?.PmsConfirmationNo,
								stages,
								propertyCode: propertyInfo?.propertyCode ?? '',
								profileID: currentProfile?.profileID ?? null,
							})
						)
							.unwrap()
							.then(() => {
								//dispatch(setGuestVerificationLazyLoad(false));
								bypassConfirmLeaveOnce();
								window.location.reload();
							});
						// Swal.fire({
						// 	title: 'Info',
						// 	text: t('Please proceed to the reception for manual check-in.'),
						// 	icon: 'info',
						// 	confirmButtonText: 'OK',
						// 	showCancelButton: false,
						// }).then(() => {
						// 	// bypassConfirmLeaveOnce();
						// 	// window.location.reload();

						// });
					});

				// // 2️⃣ Update verification status
				// await dispatch(
				// 	upsertProfileVerificationStatusThunk({
				// 		token,
				// 		profile: {
				// 			...currentProfile,
				// 			VerificationStatus: 'Completed',
				// 		},
				// 	})
				// ).unwrap();

				// // dispatch(setGuestVerificationLazyLoad(false));
				// setLazyLoad(false);

				// Swal.fire({
				// 	title: 'Info',
				// 	text: t('Please proceed to the reception for manual check-in.'),
				// 	icon: 'info',
				// 	confirmButtonText: 'OK',
				// 	showCancelButton: false,
				// }).then(() => {
				// 	bypassConfirmLeaveOnce();
				// 	window.location.reload();
				// });
			} catch (error) {
				console.error(error);
				setShowError(true);
			} finally {
				setLazyLoad(false);
			}

			return;
		}

		// 👦 Age 12–17 → OTP flow
		if (selectedAge.value === '12-17') {
			handleRequestOtp(); // this handles its own loading
		}
	};
	const handleOtpChange = (value: string, index: number) => {
		if (!/^\d?$/.test(value)) return;

		if (showResendSuccess) {
			setShowResendSuccess(false);
		}

		const newOtp = [...otp];
		newOtp[index] = value;
		setOtp(newOtp);

		// Auto-focus next input
		const nextInput = document.getElementById(`otp-${index + 1}`);
		if (value && nextInput) nextInput.focus();
	};

	const handleOtpKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>,
		index: number
	) => {
		if (e.key === 'Backspace') {
			if (showResendSuccess) {
				setShowResendSuccess(false);
			}

			// If this box has a digit, clear it (do not jump focus yet).
			if (otp[index] !== '') {
				e.preventDefault();
				const newOtp = [...otp];
				newOtp[index] = '';
				setOtp(newOtp);
				return;
			}

			// If empty, move back and clear previous digit.
			if (index > 0) {
				e.preventDefault();
				const newOtp = [...otp];
				newOtp[index - 1] = '';
				setOtp(newOtp);
				const prevInput = document.getElementById(`otp-${index - 1}`);
				if (prevInput) prevInput.focus();
			}
			return;
		}

		if (e.key === 'ArrowLeft' && index > 0) {
			e.preventDefault();
			const prevInput = document.getElementById(`otp-${index - 1}`);
			if (prevInput) prevInput.focus();
			return;
		}

		if (e.key === 'ArrowRight' && index < otp.length - 1) {
			e.preventDefault();
			const nextInput = document.getElementById(`otp-${index + 1}`);
			if (nextInput) nextInput.focus();
		}
	};

	const handleOtpPaste = (
		e: React.ClipboardEvent<HTMLInputElement>,
		index: number
	) => {
		e.preventDefault();
		if (showResendSuccess) {
			setShowResendSuccess(false);
		}

		const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
		if (!pasted) return;

		const newOtp = [...otp];
		let writeIndex = index;

		for (const digit of pasted) {
			if (writeIndex > newOtp.length - 1) break;
			newOtp[writeIndex] = digit;
			writeIndex += 1;
		}

		setOtp(newOtp);

		const focusIndex = Math.min(writeIndex, newOtp.length - 1);
		const focusInput = document.getElementById(`otp-${focusIndex}`);
		if (focusInput) focusInput.focus();
	};

	const resetWrongOtpAttempts = () => {
		setWrongOtpAttempts(0);
		setOtpError(false);
	};

	const incrementWrongOtpAttempts = () => {
		setWrongOtpAttempts((prev) => Math.min(prev + 1, MAX_WRONG_OTP_ATTEMPTS));
	};

	useEffect(() => {
		if (!otpExpiry) return;

		const timer = setInterval(() => {
			const secondsLeft = Math.max(
				Math.floor((otpExpiry - Date.now()) / 1000),
				0
			);
			setOtpCountdown(secondsLeft);

			if (secondsLeft <= 0) {
				clearInterval(timer);
				setOtpError(false);
				setSubmissionMessage('OTP has expired. Please request a new one.');
			}
		}, 1000);

		return () => clearInterval(timer);
	}, [otpExpiry]);

	const handleRequestOtp = () => {
		if (!isEmailValid) {
			setEmailError('Please enter a valid email address.');
			setShowError(false);
			return;
		}

		setLazyLoad(true);
		setShowError(false);
		setEmailError('');

		dispatch(
			requestOtpThunk({ token, email, profileID: currentProfile?.profileID })
		)
			.unwrap()
			.then((res) => {
				if (res?.result === true) {
					resetWrongOtpAttempts();
					setShowOtpScreen(true);
					setOtp(['', '', '', '']);

					// Set OTP expiry (fallback to default if API shape changes)
					const expirySeconds = getExpirySeconds(res);
					const expiryTime = Date.now() + expirySeconds * 1000;
					setOtpExpiry(expiryTime);
					setOtpCountdown(expirySeconds);
				} else {
					setShowError(true);
				}
			})
			.catch(() => {
				setShowError(true);
			})
			.finally(() => {
				setLazyLoad(false);
			});
	};

	const handleResendOtp = () => {
		if (isResendingOtp || resendCooldown > 0) return;
		if (!isEmailValid) {
			setEmailError('Please enter a valid email address.');
			setShowError(false);
			return;
		}

		setIsResendingOtp(true);
		setShowError(false);
		setOtpError(false);
		setEmailError('');
		setShowResendSuccess(false);

		dispatch(
			requestOtpThunk({ token, email, profileID: currentProfile?.profileID })
		)
			.unwrap()
			.then((res) => {
				if (res?.result === true) {
					resetWrongOtpAttempts();
					setShowOtpScreen(true);
					setOtp(['', '', '', '']);
					setResendCooldown(RESEND_COOLDOWN_SECONDS);
					setShowResendSuccess(true);
					setSubmissionMessage('');
					setOtpError(false);

					// Reset OTP expiry
					const expirySeconds = getExpirySeconds(res);
					const expiryTime = Date.now() + expirySeconds * 1000;
					setOtpExpiry(expiryTime);
					setOtpCountdown(expirySeconds);

					setTimeout(() => {
						const firstInput = document.getElementById('otp-0');
						if (firstInput) firstInput.focus();
					}, 0);
				} else {
					setShowError(true);
				}
			})
			.catch(() => {
				setShowError(true);
			})
			.finally(() => {
				setIsResendingOtp(false);
			});
	};
	const handleSendPrecheckinLink = async () => {
		console.log('handleSendVerificationLink called');
		setLazyLoad(true);
		setShowError(false);
		setEmailError('');

		const emailModel = {
			ToEmail: email,
			ConfirmationNo: reservation?.PmsConfirmationNo,
			TemplateName: 'PrecheckinTemplate',
			TemplateObject: {
				PMSConfirmationNo: reservation?.PmsConfirmationNo,
				GuestName: currentProfile?.firstName,
				ProfileID: currentProfile?.profileID,
			},
		};

		console.log('sendPrecheckinLinkThunk called', emailModel);

		try {
			const res = await dispatch(
				sendPrecheckinLinkThunk({ token, emailModel })
			).unwrap();
			console.log('sendPrecheckinLinkThunk response:', res);
			console.log('response link:', res.link);

			if (res?.isSuccess === true) {
				// after sending the email:
				// const updateResponse =
				// 	await updateReservationProfileForPrecheckinLink();
				// if (updateResponse?.result !== true) {
				// 	setShowError(true);
				// 	return;
				// } else {

				// }
				setLinkSent(true);
				setPrecheckinLink(res.link);
				if (currentProfile) {
					dispatch(
						setCurrentProfile({
							...currentProfile,
							email,
						})
					);
				}
			} else {
				setShowError(true);
			}
		} catch (err) {
			console.error('sendPrecheckinLink flow failed:', err);
			setShowError(true);
		} finally {
			setLazyLoad(false);
		}
	};

	const handleOtpVerification = async () => {
		if (wrongOtpAttempts >= MAX_WRONG_OTP_ATTEMPTS) {
			return;
		}
		setLazyLoad(true);
		setIsVerifyingOtp(true);
		setShowError(false);
		setOtpError(false);

		try {
			const res = await dispatch(
				verifyOtpThunk({
					token,
					email,
					otp: otp.join(''),
					profileID: currentProfile?.profileID ?? undefined,
				})
			).unwrap();

			const isOtpVerified = res?.data?.result === true || res?.result === true;

			if (!isOtpVerified) {
				setOtpError(true);
				incrementWrongOtpAttempts();
				setLazyLoad(false);
				setIsVerifyingOtp(false);
				return;
			}
		} catch {
			incrementWrongOtpAttempts();
			setOtpError(true);
			setLazyLoad(false);
			setIsVerifyingOtp(false);
			return;
		}

		try {
			// const updateResponse = await updateReservationProfileFun();
			// if (updateResponse?.result !== true) {
			// 	setShowError(true);
			// 	setLazyLoad(false);
			// 	setIsVerifyingOtp(false);
			// 	return;
			// }

			if (currentProfile) {
				const { firstName, lastName } = splitFullName(fullName);
				dispatch(
					setCurrentProfile({
						...currentProfile,
						firstName,
						lastName,
						fullName: fullName.trim(),
						email,
					})
				);
			}
			await dispatch(
				completeStage({
					token,
					stageID: stage?.stepID,
					ReservationID: reservation?.ReservationID,
					type: 'checkin',
					confirmationNo: reservation?.PmsConfirmationNo,
					stages,
					propertyCode: propertyInfo?.propertyCode ?? '',
					profileID: currentProfile?.profileID ?? null,
				})
			).unwrap();
			// Keep loading screen visible; next stage/page should take over.
		} catch (error) {
			console.error('Error completing stage:', error);
			setShowError(true);
			setLazyLoad(false);
			setIsVerifyingOtp(false);
		}
	};

	// const updateEmailProfileFun = async () => {
	// 	const params = {
	// 		token,
	// 		reservationID: reservation?.ReservationID,
	// 		profileID: currentProfile?.profileID,
	// 		pmsConfirmationNo: reservation?.PmsConfirmationNo,
	// 		email
	// 	};
	// 	// return dispatch(updateReservation(params)).unwrap();
	// 	const updateResponse = await updateReservationProfileApi(params);
	// 	return updateResponse?.data ?? null;
	// };

	// const updateReservationProfileForPrecheckinLink = async () => {
	// 	const { firstName, lastName } = splitFullName(fullName);
	// 	const params = {
	// 		token,
	// 		reservationID: reservation?.ReservationID,
	// 		profileID: currentProfile?.profileID,
	// 		pmsConfirmationNo: reservation?.PmsConfirmationNo,
	// 		title: currentProfile?.title,
	// 		firstName,
	// 		middleName: currentProfile?.middleName ?? '',
	// 		lastName,
	// 		email,
	// 		phoneNo_Res: currentProfile?.phoneNo_Res ?? '',
	// 	};
	// 	const updateResponse = await updateReservationProfileApi(params);
	// 	return updateResponse?.data ?? null;
	// };

	const handleEmailChange = (value: string) => {
		// 1. remove Arabic characters immediately
		const cleaned = value.replace(arabicCharactersPattern, '');

		setEmail(cleaned);

		// 2. Arabic check (if user tried to type them)
		if (arabicCharactersPattern.test(value)) {
			setEmailError('Arabic letters are not allowed in the email address.');
			return;
		}

		// 3. empty state → no error
		if (!cleaned.trim()) {
			setEmailError('');
			return;
		}

		// 4. email validation
		if (!emailPattern.test(cleaned.trim())) {
			setEmailError('Invalid email address');
			return;
		}

		// 5. valid email
		setEmailError('');
	};

	const renderEmailInput = ({
		label,
		wrapperClassName = '',
		inputClassName = 'form-control',
		placeholder = '',
	}: {
		label: string;
		wrapperClassName?: string;
		inputClassName?: string;
		placeholder?: string;
	}) => (
		<div className={wrapperClassName}>
			<label className={`${styles.text_secondary} mb-1`}>{label}</label>
			{/* <input
				type="text"
				className={inputClassName}
				value={email}
				disabled={lazyLoad}
				onChange={(e) => handleEmailChange(e.target.value)}
				onFocus={(e) => {
					const target = e.target as HTMLInputElement;
					const len = target.value.length;
					// type="email" doesn't support setSelectionRange/selectionStart/selectionEnd
					// Changing type to "text" (and using inputMode="email") solves this.
					try {
						target.setSelectionRange(len, len);
					} catch (err) {
						console.error('setSelectionRange failed:', err);
					}
				}}
				onClick={(e) => {
					const target = e.target as HTMLInputElement;
					const len = target.value.length;
					try {
						target.setSelectionRange(len, len);
					} catch (err) {
						console.error('setSelectionRange failed:', err);
					}
				}}
				inputMode="email"
				autoComplete="email"
				placeholder={placeholder}
			/> */}
			<input
				type="text"
				inputMode="email"
				className={inputClassName}
				value={email}
				disabled={lazyLoad}
				onChange={(e) => handleEmailChange(e.target.value)}
				onFocus={(e) => {
					const target = e.target;
					const len = target.value.length;
					target.setSelectionRange(len, len);
				}}
				onBlur={(e) => handleEmailChange(e.target.value)}
			/>
			{emailError && (
				<small className="text-danger mt-2 d-block">{emailError}</small>
			)}
		</div>
	);

	const renderNameInput = ({
		label,
		placeholder = 'Full Name',
		value,
	}: {
		label: string;
		placeholder?: string;
		value?: string;
	}) => (
		<div className={styles.fieldGroup}>
			<label className={styles.text_secondary}>{label}</label>
			<input
				type="text"
				className="form-control"
				placeholder={placeholder}
				disabled={lazyLoad}
				value={value}
				onChange={(e) => handleNameChange(e.target.value)}
			/>
			{nameError && (
				<small className="text-danger mt-2 d-block">{nameError}</small>
			)}
		</div>
	);
	const nameRegex = /^[A-Za-z\s]*$/;
	const handleNameChange = (value: string) => {
		setFullName(value);

		// empty check
		if (!value.trim()) {
			setNameError('Full name is required');
			return;
		}

		// special characters / numbers check
		if (!nameRegex.test(value)) {
			setNameError('Only letters are allowed');
			return;
		}

		setNameError('');
	};
	const isChildInvalid =
		isChild &&
		(!selectedAge || (selectedAge?.value === '12-17' && !parentalConsent));

	const isOtpAttemptLocked = wrongOtpAttempts >= MAX_WRONG_OTP_ATTEMPTS;
	const isOtpComplete = otp.every((digit) => digit !== '');
	const requiresNameInput =
		isChild || (guestWithMe !== false && !currentProfile?.isPrimary);

	// const isButtonDisabled =
	// 	lazyLoad ||
	// 	isResendingOtp ||
	// 	(!showOtpScreen && email?.trim() === '') ||
	// 	(emailError && emailError.length > 0) ||
	// 	(!showOtpScreen && isChildInvalid) ||
	// 	(showOtpScreen &&
	// 		(!isOtpComplete ||
	// 			isOtpAttemptLocked ||
	// 			Boolean(otpExpiry && otpCountdown <= 0)));

	const isButtonDisabled = useMemo(() => {
		if (lazyLoad || isResendingOtp) return true;

		// Email + Name step
		if (!showOtpScreen) {
			return (
				!email?.trim() ||
				Boolean(emailError) ||
				(requiresNameInput && (!fullName?.trim() || Boolean(nameError))) ||
				isChildInvalid
			);
		}

		// OTP step
		return (
			!isOtpComplete ||
			isOtpAttemptLocked ||
			Boolean(otpExpiry && otpCountdown <= 0)
		);
	}, [
		lazyLoad,
		isResendingOtp,
		showOtpScreen,
		fullName,
		email,
		emailError,
		nameError,
		isChildInvalid,
		isOtpComplete,
		isOtpAttemptLocked,
		otpExpiry,
		otpCountdown,
		requiresNameInput,
	]);

	const isResendDisabled = isResendingOtp || resendCooldown > 0;

	if (isVerifyingOtp) {
		return null;
	}
	return (
		<div
			className={`d-flex flex-column mx-auto ${styles.otpRoot}`}
			style={{
				minHeight: 'calc(100dvh - 120px)',
				overflowX: 'hidden',
			}}>
			<div
				className={`d-flex justify-content-center align-items-center w-100 theme-header ${styles.themeHeader}`}
				style={{ minHeight: '80px' }}
			/>

			<div className={`mx-auto w-100 ${styles.otpBody}`}>
				{!showOtpScreen && (
					<div
						className={`d-flex flex-column align-items-center text-center ${styles.contentStack}`}>
						<div
							className={`d-flex justify-content-center ${styles.otpImageContainer}`}>
							<img
								src={process.env.PUBLIC_URL + '/otp-validation.png'}
								alt="OTP"
								className={styles.otpImage}
							/>
						</div>

						<div className={styles.formCard}>
							<div className={styles.cardHeader}>
								<h4 className={`fw-semibold mb-1 ${styles.pageTitle}`}>
									{currentProfile?.isPrimary === true
										? t('PrimaryGuestDetails')
										: t('GuestDetails')}
								</h4>
								<p
									className={`${styles.text_subtext} text-muted small mb-0 ${styles.pageSubtitle}`}>
									{isChild
										? t('GuesDetailsEnter')
										: currentProfile?.isPrimary === true
										? t('EnterPrimaryGuestEmailId')
										: t('EnterGuestEmailId')}
								</p>
							</div>

							{isChild ? (
								<div
									className={`d-flex flex-column align-items-start text-start ${styles.formSection}`}>
									<div className={styles.childFlexRow}>
										<div className={styles.childFlexItem}>
											<div className={styles.fieldGroup}>
												<label className={styles.text_secondary}>
													{t('GuestDetailsSelectAge')}
												</label>

												<Select<AgeOption, false>
													options={ageOptions}
													value={selectedAge}
													onChange={(option) => {
														setSelectedAge(option);
													}}
													placeholder={t('GuestDetailsDropdownSelectAgeGroup')}
													isSearchable={false}
													styles={searchableSelectStyles}
													menuPortalTarget={document.body}
													isDisabled={lazyLoad}
												/>
											</div>
										</div>

										<div className={styles.childFlexItem}>
											<div className={styles.fieldGroup}>
												<label className={styles.text_secondary}>
													{t('GuestDetailsParent')}
												</label>

												<Select
													options={parentOptions}
													value={selectedParent}
													onChange={(option) =>
														setSelectedParentId(option?.value ?? null)
													}
													placeholder={
														t('GuestDetailsParent') ||
														'Select Parent / Guardian'
													}
													isSearchable={false}
													className={styles.selectControl}
													styles={searchableSelectStyles}
													menuPortalTarget={document.body}
													isDisabled={lazyLoad}
												/>
											</div>
										</div>
									</div>

									<div className={styles.childFlexRow}>
										<div className={styles.childFlexItem}>
											{renderEmailInput({
												label: t('EmailId'),
												wrapperClassName: `w-100 ${styles.otpEmailWrapper}`,
												inputClassName: 'form-control form-control-lg',
											})}
										</div>
										<div className={styles.childFlexItem}>
											{renderNameInput({
												label: t('GuestDetailsChildName'),
												value: fullName,
											})}
										</div>
									</div>

									{selectedAge?.value === '1-11' && (
										<>
											<div className="flex gap-2 items-start p-2 mt-2 text-left bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-800">
												<div className="mt-1">
													<FontAwesomeIcon icon={faInfoCircle} />
												</div>

												<div className="text-start">
													<p className="text-black">
														Verification for guests under 12 must be completed
														in person. Please proceed to the reception desk upon
														arrival.
													</p>
												</div>
											</div>
										</>
									)}
									{selectedAge?.value === '12-17' && (
										<div
											className={`${styles.fieldGroup} ${styles.childFullWidth}`}>
											<label className={styles.text_secondary}>
												Parental Consent <span className="text-danger">*</span>
											</label>
											<div className="form-check">
												<input
													className="form-check-input"
													type="checkbox"
													id="parental-consent"
													checked={parentalConsent}
													onChange={(e) => {
														setParentalConsent(e.target.checked);
													}}
												/>
												<label
													className="form-check-label"
													htmlFor="parental-consent">
													I confirm that I am the parent or legal guardian of
													this minor and consent to the scanning of their face
													and identification document for verification purposes.
												</label>
											</div>
										</div>
									)}

									{submissionMessage && (
										<div className={`w-100 ${styles.childFullWidth}`}>
											<small className="text-danger">{submissionMessage}</small>
										</div>
									)}
								</div>
							) : (
								<>
									<div
										className={`d-flex flex-column align-items-start text-start ${styles.formSection}`}>
										{currentProfile?.isPrimary !== true &&
											guestWithMe !== false &&
											renderNameInput({
												label: t('GuestDetailsGuest2'),
												value: fullName,
											})}

										{renderEmailInput({
											label: t('EmailId'),
											wrapperClassName: `w-100 ${styles.otpEmailWrapper}`,
											inputClassName: 'form-control form-control-lg',
										})}

										{showError && (
											<div className="mt-2">
												<small className="text-danger">
													{t('ErrorOtpSending')}
												</small>
											</div>
										)}
									</div>
								</>
							)}
						</div>
					</div>
				)}

				{showOtpScreen && (
					<div className={styles.formCard}>
						<div className={styles.otpVerifyWrap}>
							<div
								className={`d-flex justify-content-center ${styles.otpLogoWrap}`}>
								<img
									src={process.env.PUBLIC_URL + '/otp-lock.svg'}
									alt="OTP"
									className={styles.otp_lock_image}
								/>
							</div>

							<div className={`mt-3 ${styles.otpCopy}`}>
								<strong>{t('EnterOtp')}</strong>
							</div>
							<small className={`text-muted d-block mt-1 ${styles.otpCopy}`}>
								{t('PleaseEnterOtp')}
							</small>

							<div
								className={`d-flex justify-content-center gap-2 mt-4 ${styles.otp_input_row}`}>
								{otp.map((digit, index) => (
									<input
										key={index}
										id={`otp-${index}`}
										disabled={lazyLoad}
										name={`otp-${index}`}
										type="text"
										inputMode="numeric"
										pattern="[0-9]*"
										maxLength={1}
										autoComplete="off"
										autoCorrect="off"
										spellCheck={false}
										className={`form-control text-center ${styles.otp_input}`}
										value={digit}
										onChange={(e) => handleOtpChange(e.target.value, index)}
										onKeyDown={(e) => handleOtpKeyDown(e, index)}
										onPaste={(e) => handleOtpPaste(e, index)}
									/>
								))}
							</div>

							{showError && (
								<div className={`mt-2 ${styles.otpCopy}`}>
									<small className="text-danger">{t('ErrorOtpSending')}</small>
								</div>
							)}

							{otpError && !isOtpAttemptLocked && !isOtpExpired && (
								<div className={`mt-2 ${styles.otpCopy}`}>
									<small className="text-danger">{t('OtpError')}</small>
								</div>
							)}

							{isOtpAttemptLocked && (
								<div className={`mt-2 ${styles.otpCopy}`}>
									<small className="text-danger">
										{t('OtpAttemptsExceeded')}
									</small>
								</div>
							)}
							{otpExpiry && otpCountdown > 0 && (
								<small className="d-block mt-2 text-muted">
									OTP expires in {otpCountdown} seconds
								</small>
							)}

							{otpExpiry && otpCountdown <= 0 && (
								<small className="text-danger d-block mt-2">
									OTP expired. Please request a new one.
								</small>
							)}
							<small className={`d-block mt-3 ${styles.otpCopy}`}>
								{t('DontReceiveOtp')}{' '}
								<span
									className={`${styles.resendLink} ${
										isResendDisabled ? styles.resendDisabled : ''
									}`}
									role="button"
									aria-disabled={isResendDisabled}
									onClick={handleResendOtp}>
									{isResendDisabled
										? resendCooldown > 0
											? t('ResendOtpIn', { seconds: resendCooldown })
											: t('SendingOtp')
										: t('ResendOtp')}
								</span>
							</small>

							{showResendSuccess && (
								<small className="d-block text-center text-success mt-2">
									{t('OtpResent')}
								</small>
							)}
						</div>
					</div>
				)}

				<PrecheckinLinkSentModal
					show={linkSent}
					title="Success"
					message="Precheck-in link has been sent to your email."
					onClose={() => {
						bypassConfirmLeaveOnce();
						window.location.reload();
					}}
					buttonText="Ok"
					precheckinLink={precheckinLink || ''}
				/>
			</div>

			<div
				className={`fixed-bottom border-top   d-flex justify-content-center theme-footer ${styles.footerBar}`}>
				{/* <DarkButton
					text={
						!showOtpScreen && guestWithMe === false
							? t('SendLink')
							: showOtpScreen
							? t('Next')
							: t('Next')
					}
					filled
					hideArrow
					override={lazyLoad} // ✅ disable loading if message exists
					onClick={() => {
						console.log('Button clicked', {
							showOtpScreen,
							guestWithMe,
							isChild,
							lazyLoad,
						});
						if (!showOtpScreen && guestWithMe === false) {
							handleSendPrecheckinLink();
						} else if (!showOtpScreen) {
							if (isChild) {
								handleChildSubmission();
								console.log('After child submission', {
									lazyLoad,
									childSubmissionMessage,
								});
							} else {
								handleRequestOtp();
							}
						} else {
							handleOtpVerification();
						}
					}}
				/> */}

				<DarkButton
					text={
						selectedAge?.value === '1-11'
							? t('VerifyAtReception')
							: !showOtpScreen && guestWithMe === false
							? t('SendLink')
							: showOtpScreen
							? t('Next')
							: t('Next')
					}
					filled
					hideArrow
					override={lazyLoad}
					loader={lazyLoad}
					disabled={isButtonDisabled}
					onClick={() => {
						if (!showOtpScreen && guestWithMe === false) {
							handleSendPrecheckinLink();
						} else if (!showOtpScreen) {
							if (isChild) {
								handleChildSubmission();
							} else {
								handleRequestOtp();
							}
						} else {
							handleOtpVerification();
						}
					}}
				/>
			</div>
		</div>
	);
};

export default GuestOtpValidationForm;
